/**
 * 日本語翻訳
 */
import en, { type TranslationKeys } from './en';

type StringShape<T> = {
  [K in keyof T]: T[K] extends string ? string : StringShape<T[K]>;
};

const ja: StringShape<TranslationKeys> = {
  // ── App shell ──
  app: {
    name: 'Boox Platform',
    logo: 'B',
  },
  tabs: en.tabs,
  commonAction: {
    refresh: '更新',
    runNow: '今すぐ実行',
    newTask: '新規タスク',
    newLocalService: '新規ローカルサービス',
    create: '作成',
    save: '保存',
  },
  hmsMatchbox: en.hmsMatchbox,
  hmsPricing: en.hmsPricing,
  hmsRates: en.hmsRates,
  shippingAgency: {
    accounts: {
      pageTitle: '船舶代理アカウント',
      activeAccounts: '有効アカウント',
      creditHold: '与信保留',
      chargeProfiles: 'ローカル料金設定',
    },
    principalTasks: {
      queueTitle: 'Principal タスクデスク',
      queued: '待機中',
      running: '実行中',
      needsOverride: '手動介入待ち',
      empty: 'principal 連携タスクはありません。',
      runReady: '実行可能な認証ブラウザ計画を作成済み',
    },
    localServices: {
      pageTitle: 'ローカルサービスデスク',
      truckingOrders: 'トラック手配',
      depotOrders: 'デポ作業',
      backlog: '未処理サービス backlog',
      revenuePlanned: '計画売上',
      marginPlanned: '計画粗利',
      empty: 'ローカルサービス注文はありません。',
      filtered: 'Booking {{bookingNo}} で絞り込み済み',
      createTitle: 'ローカルサービス注文を作成',
      loadingQueue: 'ローカルサービス調整キューを読み込み中…',
      vendor: '業者',
      container: 'コンテナ',
      scheduled: '予定',
      revenue: '売上',
      cost: '原価',
      margin: '粗利',
      customerBilling: '顧客請求',
      vendorCost: '業者費用',
      openArDraft: 'AR 下書きを開く',
      openApDraft: 'AP 下書きを開く',
      openServiceDesk: 'サービスオーダーデスクを開く',
      openDepotMoves: 'デポ move デスクを開く',
      generateArDraft: 'AR 下書きを生成',
      generateApDraft: 'AP 下書きを生成',
      arDraftReady: 'AR 下書き作成済み',
      apDraftReady: 'AP 下書き作成済み',
      postCustomerInvoice: '顧客請求を計上',
      postVendorBill: '業者請求を計上',
      confirmReceipt: '入金を確認',
      confirmVendorPayment: '支払を確認',
      customerInvoicePosted: '顧客請求を計上しました',
      vendorBillPosted: '業者請求を計上しました',
      customerReceiptConfirmed: '顧客入金を確認しました',
      vendorPaymentConfirmed: '業者支払を確認しました',
      settlementActionFailed: '精算処理の実行に失敗しました',
      notStarted: '未開始',
      draftReady: '下書き準備完了',
      posted: '計上済み',
      partiallySettled: '一部精算済み',
      settled: '精算済み',
      voided: '無効',
      pending: '保留',
    },
    emailIntake: {
      pageTitle: 'AI メール Intake',
      subtitle: '代理店メールを booking、書類、release、ローカルサービス、Evergreen 下書きに変換します。',
      from: '差出人',
      to: '宛先',
      subject: '件名',
      body: 'メール本文',
      autoCreate: '下書き work item を自動作成',
      submit: 'メールを処理',
      result: '処理結果',
      draftKind: '下書き種別',
      linkedBooking: '関連 booking',
    },
  },
  depotModule: {
    dashboardTitle: 'Depot オペレーション概要',
    dashboardSubtitle: 'Gate、yard、EIR、inspection、move、billing を一つのデスクで管理。',
  },
  socModule: {
    dashboardTitle: 'SOC コンテナ管制塔',
    dashboardSubtitle: '箱プール、release、overdue return、charge exposure を一画面で確認。',
  },

  // ── Login ──
  login: {
    title: 'Boox Platform',
    subtitle: 'ログインして続ける',
    email: 'メールアドレス',
    loginName: 'ログイン名',
    password: 'パスワード',
    submit: 'ログイン',
    submitting: 'ログイン中…',
    failed: 'ログインに失敗しました',
    heroTitle: 'インテリジェントエンタープライズオペレーティングシステム',
    heroDesc: '業務を自動化し、予約を追跡し、運用ワークフロー全体を一箇所で管理します。',
    feature1: 'リアルタイム追跡',
    feature2: 'スマート自動化',
    feature3: 'マルチテナント対応',
    welcomeBack: 'おかえりなさい',
    forgotPassword: 'パスワードをお忘れですか？',
    securityNote: '接続は暗号化されており、安全です。',
    back: '戻る',
    selectTenant: 'ワークスペースを選択',
    brandingLoading: 'テナントブランド情報を読み込み中です。しばらくしてから再試行してください。',
    secureAccessFor: '{{company}} への安全なアクセス',
    workspaceOnHost: '{{host}} 上の {{portal}}',
    tenantWorkspace: 'テナントワークスペース',
    shortAccountHint: 'ここでは短いアカウント名が使えます。例: {{hint}}',
    dedicatedLogin: 'このログインは {{company}} 専用です。',
    unauthorizedTenant: 'このアプリケーションにアクセスする権限がありません。',
    noTenantAccess: 'このアカウントにはアクセス可能なテナントがありません。',
    invalidCredentials: 'ユーザー名またはパスワードが正しくありません。',
    bridgeFailed: 'ログインの続行に失敗しました',
  },

  // ── Dashboard ──
  dashboard: {
    title: 'ダッシュボード',
    welcome: 'おかえりなさい、{{name}}',
    goodMorning: 'おはようございます',
    goodAfternoon: 'こんにちは',
    goodEvening: 'こんばんは',
    fields: 'フィールド',
    children: '子要素',
    relations: '関連',
  },

  // ── Per-category workspace pages ──
  workspace: {
    overview:        '概要',
    subtitle:        '{{count}} エンティティ',
    quickLinks:      'クイックリンク',
    noEntities:      'このワークスペースにはエンティティがありません',
    noEntitiesDesc:  '管理パネルでこのカテゴリのエンティティを設定してください。',
    notFound:        'ワークスペースが見つかりません',
    notFoundDesc:    'このワークスペースは設定されていないか、アクセス権限がありません。',
  },

  // ── Sidebar category labels ──
  category: {
    // ── Core ──
    home:         'ホーム',
    builder:      'Business Builder',
    inbox:        '受信トレイ',
    business:     'ビジネス',
    // ── Verticals ──
    transport:    'クラシック輸送',
    fleet:        'フリート',
    freight:      '貨物パイロット',
    warehouse:    '倉庫フロー',
    haulage:      'コンテナ輸送コマンド',
    'supply-chain': 'サプライチェーンナビゲーター',
    service:      'サービス管理',
    // ── Support ──
    finance:      '財務',
    workforce:    '人事',
    // ── Admin (bottom-pinned) ──
    settings:     '設定',
    platform:     'プラットフォーム',
    // ── Legacy (backward compat) ──
    tms:          'TMS',
    fms:          'FMS',
    wms:          'WMS',
    accounting:   '会計',
    ai:           'AI',
    'ai-command': 'AIコマンド',
    trucking:     'トラック輸送',
    accounts:     '会計',
    parties:      '取引先',
    messaging:    'メッセージ',
    agent:        'エージェント',
    config:       '設定',
    master:       'マスターデータ',
    transaction:  'オペレーション',
    event:        'イベント',
    attachment:   '添付ファイル',
    communication:'コミュニケーション',
    pro2:         'CMS PRO2',
    audit:        '監査 & コンプライアンス',
    // ── Network ──
    network:      'Boox ネットワーク',
    tenants:      'テナント管理',
    'ai-center':  'AIセンター',
    'cc-home':      'CC ホーム',
    'cc-tenants':   'テナント運用',
    'cc-brain':     'AI & ブレイン',
    'cc-builder':   'ビルダー',
    'cc-observe':   'オブザーバビリティ',
    'cc-analytics': 'アナリティクス',
    'cc-command':   'Command',
    'cc-revenue':   'Revenue Ops',
    'cc-delivery':  'Delivery Ops',
    'cc-ceo':       'CEO',
    'cc-tenant-admin': 'Tenant Admin',
    'cc-systems':   'Platform Systems',
    sas:          '船舶代理店',
    depot:        'デポコントロール',
    soc:          'SOC',
  },

  // ── Sidebar group labels ──
  group: {
    // ── Home groups ──
    Dashboards:       'ダッシュボード',
    'Quick Access':   'クイックアクセス',
    // ── Inbox groups ──
    Messages:         'メッセージ',
    Tasks:            'タスク',
    // ── Business groups ──
    Sales:            '販売',
    Purchase:         '仕入',
    // ── Transport / Freight / Warehouse ──
    Operations:       '操作',
    Oversea:          '海外',
    // ── Finance groups ──
    AR:               '売掛金',
    AP:               '買掛金',
    GL:               '総勘定',
    // ── Workforce groups ──
    People:           '人員',
    Branches:         '支店',
    Access:           'アクセス',
    // ── Settings groups ──
    Tenant:           'テナント',
    System:           'システム',
    // ── Platform groups ──
    'AI Admin':       'AI管理',
    DevOps:           'DevOps',
    // ── CC (Command Control) groups ──
    Tenants:          'テナント',
    Channels:         'チャンネル',
    Security:         'セキュリティ',
    Rules:            'ルール',
    'AI Config':      'AI設定',
    Brain:            'ブレイン',
    Logs:             'ログ',
    Dev:              '開発',
    Infra:            'インフラ',
    'Platform Intelligence': 'プラットフォームインテリジェンス',
    overview:         'Overview',
    runtime:          'Runtime',
    tools:            'Tools',
    intake:           'Intake',
    qualification:    'Qualification',
    governance:       'Governance',
    delivery:         'Delivery',
    readiness:        'Readiness',
    executive:        'Executive',
    configuration:    'Configuration',
    build:            'Build',
    'ai-runtime':     'AI Runtime',
    systems:          'Systems',
    monitoring:       'Monitoring',
    // ── Shared groups ──
    Setup:            'セットアップ',
    Reports:          'レポート',
    'Master Data':    'マスターデータ',
    Config:           '設定',
    Monitor:          'モニター',
    // ── Legacy group keys ──
    'Container Trucking': 'コンテナトラック',
    'Local Transport':    'ローカル輸送',
    Sea:              '海上',
    Air:              '航空',
    'Local Handling': 'ローカル操作',
    Customers:        '顧客',
    'Business Parties': '取引先',
    Employees:        '従業員',
    AI:               'AI',
    Driver:           'ドライバー',
    Workflow:         'ワークフロー',
    work:             '作業',
    setup:            'セットアップ',
    transactions:     'トランザクション',
    inventory:        '在庫',
    reports:          'レポート',
    tax:              '税務',
    customers:        '顧客',
    partners:         'パートナー',
    team:             'チーム',
    inbox:            '受信トレイ',
    email:            'メール',
    channels:         'チャンネル',
    pipeline:         'パイプライン',
    tasks:            'タスク',
    files:            'ファイル',
    audit:            '監査',
    ui:               'UIテンプレート',
    system:           'システム',
    // ── Entity category labels ──
    people:       '人物',
    jobs:         'ジョブ',
    shipping:     '出荷',
    finance:      '財務',
    warehouse:    '倉庫',
    messaging:    'メッセージ',
    'inventory-events': '在庫イベント',
    // ── Module nav group labels (lowercase, used by MODULE_NAV_ITEMS) ──
    dashboard:        'ダッシュボード',
    orders:           '注文',
    operations:       'オペレーション',
    import:           '輸入',
    export:           '輸出',
    master:           'マスター',
    Import:           '輸入',
    Export:           '輸出',
    Master:           'マスター',
    overall:          '全体',
    operating:        '運営',
    category:         'カテゴリ',
    planning:         '計画',
    banking:          '銀行',
    admin:            '管理',
    'self-service':   'セルフサービス',
    config:           '設定',
    tenants:          'テナント',
    policies:         'ポリシー',
    agents:           'エージェント',
    knowledge:        'ナレッジ',
    schema:           'スキーマ',
    templates:        'テンプレート',
    automation:       '自動化',
    devtools:         '開発ツール',
    integration:      '連携',
    deployment:       'デプロイ',
    communication:    'コミュニケーション',
    breakdown:        '内訳',
    realtime:         'リアルタイム',
    trends:           'トレンド',
    // ── Finance module groups (new) ──
    processes:        'プロセス',
    management:       '管理レポート',
    einvoicing:       '電子インボイス',
    compliance:       'コンプライアンス',
    // ── Other new groups ──
    review:           'レビュー',
    crm:              'RM',
    messages:         'メッセージ',
    schedule:         'スケジュール',
    docs:             '書類',
    // ── Network groups ──
    collaboration:    'コラボレーション',
    // ── Module sidebar groups (used by MODULE_NAV_ITEMS) ──
    ai:               'AI',
    'boox-network':   'Boox ネットワーク',
    circles:          'サークル',
    contacts:         '連絡先',
    fulfillment:      'フルフィルメント',
    personal:         '個人',
    procurement:      '調達',
    requests:         'リクエスト',
    resources:        'リソース',
    safety:           '安全',
    sales:            '販売',
    sourcing:         'ソーシング',
    tenant:           'テナント',
    views:            'ビュー',
    "collections": "コレクション",
    "control": "制御",
    "core": "コア",
    "devops": "DevOps",
    "execution": "実行",
    "fleet": "艦隊",
    "master-data": "マスターデータ",
    "ops": "運営",
    "platform": "プラットフォーム",
    "workshop": "ワークショップ",
    "workspace": "ワークスペース",
  },

  // ── Entity list ──
  list: {
    totalRecords: '{{count}} 件',
    newBtn: '新規',
    cancel: 'キャンセル',
    createTitle: '新規 {{label}}',
    createSuccess: '{{label}} を作成しました',
    search: '{{label}} を検索…',
    allStatus: 'すべてのステータス',
    loading: '読み込み中…',
    noData: 'データなし',
    page: 'ページ {{current}} / {{total}}',
    loadFailed: 'データの読み込みに失敗しました',
    notFound: 'エンティティが見つかりません',
  },

  // ── Entity detail ──
  detail: {
    loadFailed: '詳細の読み込みに失敗しました',
    notFound: 'レコードが見つかりません',
    confirmDeleteTitle: '削除の確認',
    confirmDeleteDesc: 'この {{label}} レコードを削除してもよろしいですか？この操作は元に戻せません。',
    delete: '削除',
    deleteSuccess: '削除しました',
    deleteFailed: '削除に失敗しました',
    actionSuccess: '{{action}} を実行しました',
    actionFailed: '{{action}} に失敗しました',
    cancelEdit: '編集をキャンセル',
    edit: '編集',
    editTitle: '{{label}} を編集',
    tabHuman: '全体表示',
    tabBasic: '基本',
    tabJson: 'JSON',
  },

  // ── Entity edit form ──
  form: {
    required: '{{field}} は必須です',
    minValue: '最小値は {{min}} です',
    maxValue: '最大値は {{max}} です',
    invalidFormat: '形式が正しくありません',
    selectPlaceholder: '選択…',
    boolTrue: 'はい',
    boolFalse: 'いいえ',
    saveSuccess: '保存しました',
    createSuccess: '作成しました',
    saveFailed: '保存に失敗しました',
    save: '保存',
    create: '作成',
    cancel: 'キャンセル',
  },

  // ── Human view ──
  humanView: {
    summary: 'サマリー',
    relatedData: '関連データ',
    childEntities: '子エンティティ',
    moreRows: 'さらに {{count}} 件…',
    boolTrue: 'はい',
    boolFalse: 'いいえ',
    empty: '—',
  },

  // ── Basic view ──
  basicView: {
    basicInfo: '基本情報',
    extendedData: '詳細データ',
    boolTrue: 'はい',
    boolFalse: 'いいえ',
    empty: '—',
  },

  // ── View template fallback sections ──
  viewTemplate: {
    basicInfo: '基本情報',
    financialInfo: '財務情報',
    timeline: 'タイムライン',
    otherInfo: 'その他の情報',
  },

  // ── Export ──
  export: {
    csv: 'CSV エクスポート',
    success: 'エクスポート完了',
    failed: 'エクスポートに失敗しました。再試行してください',
  },

  // ── Notifications ──
  notification: {
    title: '通知',
    markAllRead: 'すべて既読にする',
    loading: '読み込み中…',
    empty: '通知なし',
    justNow: 'たった今',
    minutesAgo: '{{count}} 分前',
    hoursAgo: '{{count}} 時間前',
    daysAgo: '{{count}} 日前',
  },

  // ── Confirm dialog ──
  confirm: {
    ok: '確認',
    cancel: 'キャンセル',
    processing: '処理中…',
  },

  // ── Error boundary ──
  error: {
    title: 'エラーが発生しました',
    fallback: '予期しないエラーが発生しました。',
    tryAgain: '再試行',
    goHome: 'ホームへ',
  },

  // ── Search / Command Palette ──
  search: {
    placeholder: 'エンティティを検索… ⌘K',
    noResults: '一致するエンティティがありません',
    navigate: '移動',
    open: '開く',
    results: '{{count}} 件の結果',
  },

  // ── User dropdown ──
  user: {
    profile: 'プロフィール',
    settings: '設定',
    shortcuts: 'キーボードショートカット',
    signOut: 'サインアウト',
    switchTenant: '会社を切り替え',
  },

  // ── Navigation actions ──
  nav: {
    chat: 'チャット',
    home: 'ホーム',
    teamspace: 'Teamspace',
    ceoDesk: 'CEO Desk',
    booxCommand: 'Boox Command',
    aiChat: 'AI Chat',
    brainMonitor: 'Brain Monitor',
    runtimeDebug: 'Runtime Debug',
    alertCenter: 'Alert Center',
    rolloutDashboard: 'Rollout Dashboard',
    revenueInbox: 'Revenue Inbox',
    messagingIntake: 'Messaging Intake',
    surfaceAccess: 'Surface Access',
    partnerApplications: 'Partner Applications',
    onboardingControl: 'Onboarding Control',
    onboardingRecords: 'Onboarding Records',
    assignmentPolicies: 'Assignment Policies',
    capabilityMatrix: 'Capability Matrix',
    stateConsole: 'State Console',
    eInvoicingOps: 'E-Invoicing Ops',
    strategy: 'Strategy',
    fundraising: 'Fundraising',
    dataRoom: 'Data Room',
    aiCoCeo: 'AI Co-CEO',
    tenants: 'Tenants',
    globalUsers: 'Global Users',
    userRoles: 'User Roles',
    authControl: 'Auth Control',
    capabilities: 'Capabilities',
    policies: 'Policies',
    exceptionPolicies: 'Exception Policies',
    workflowPolicies: 'Workflow Policies',
    whatsAppAdmin: 'WhatsApp',
    builder: 'Builder',
    businessPackages: 'Business Packages',
    platformSystems: 'Platform Systems',
    configCompiler: 'Config Compiler',
    configVersions: 'Config Versions',
    domainConfigs: 'Domain Configs',
    menuConfig: 'Menu Config',
    bridges: 'Bridges',
    dataTransfer: 'Data Transfer',
    deployControl: 'Deploy Control',
    printCenter: 'Print Center',
    platformAnalytics: 'Platform Analytics',
    logExplorer: 'Log Explorer',
    schemaEditor: 'Schema Editor',
    dslDesigner: 'DSL Designer',
    viewTemplates: 'View Templates',
    documentTemplates: 'Document Templates',
    workflows: 'Workflows',
    workflowTemplates: 'Workflow Templates',
    scripts: 'Scripts',
    aiCommandCenter: 'AI Command Center',
    aiTeam: 'AI Team',
    agentLab: 'Agent Lab',
    skills: 'Skills',
    skillConfig: 'Skill Config',
    prompts: 'Prompts',
    trainingData: 'Training Data',
    memory: 'Memory',
    tools: 'Tools',
    aiControlPlane: 'AI Control Plane',
    brainLogs: 'Brain Logs',
    brainSoul: 'Brain Soul',
    brainSchedule: 'Brain Schedule',
    recurringTasks: 'Recurring Tasks',
    taskCenter: 'Task Center',
    executiveNudges: 'Executive Nudges',
    apiCenter: 'API Center',
    accessLog: 'Access Log',
    userPerformance: 'User Performance',
    jobTimeline: 'Job Timeline',
    pro2: {
      importShipment: '輸入貨件',
      importShipments: '輸入貨件',
      importContainer: '輸入コンテナ',
      importContainers: '輸入コンテナ',
      importCargo: '輸入貨物',
      exportShipment: '輸出貨件',
      exportShipments: '輸出貨件',
      exportBooking: '輸出ブッキング',
      exportBookings: '輸出ブッキング',
      exportContainer: '輸出コンテナ',
      exportContainers: '輸出コンテナ',
      exportCargo: '輸出貨物',
      invCostNote: '請求 / 原価ノート',
      invCostNotes: '請求 / 原価ノート',
      chargeLine: '料金明細',
      chargeLines: '料金明細',
      company: '会社マスタ',
      companies: '会社マスタ',
    },
    Dashboards: 'ダッシュボード',
    'Team Monitor': 'チームモニター',
    Shortcuts: 'ショートカット',
    Recent: '最近',
    'All Messages': 'すべてのメッセージ',
    Emails: 'メール',
    WhatsApp: 'WhatsApp',
    'My Tasks': 'マイタスク',
    Approvals: '承認',
    Alerts: 'アラート',
    Orders: '注文',
    Quotations: '見積',
    Pipelines: 'パイプライン',
    Goals: '目標',
    'Purchase Orders': '発注書',
    'Vendor Sourcing': 'ベンダー調達',
    Customers: '顧客',
    Vendors: 'ベンダー',
    Contacts: '連絡先',
    'Sales Reports': '販売レポート',
    Jobs: 'ジョブ',
    Planner: 'プランナー',
    'FCL Planner': 'FCLプランナー',
    'LCL Planner': 'LCLプランナー',
    Trips: '便',
    'Live Tracking': 'ライブ追跡',
    Dispatch: '配車',
    'Master Data': 'マスターデータ',
    Drivers: 'ドライバー',
    Vehicles: '車両',
    Routes: 'ルート',
    Billing: '請求',
    'Sea Imports': '海上輸入',
    'Sea Exports': '海上輸出',
    'Air Imports': '航空輸入',
    'Air Exports': '航空輸出',
    'Port System': '港湾システム',
    CFS: 'CFS',
    Customs: '通関',
    Documents: 'ドキュメント',
    Rates: 'レート',
    Carriers: 'キャリア',
    Vessels: '船舶',
    Accounting: '会計',
    'Volume Reports': '数量レポート',
    Inbound: '入庫',
    Inventory: '在庫',
    Outbound: '出庫',
    'Work Orders': '作業指示',
    Warehouses: '倉庫',
    Locations: 'ロケーション',
    'Stock Reports': '在庫レポート',
    Movements: '移動',
    Invoices: '請求書',
    Receipts: '領収書',
    'AR Aging': '売掛金エイジング',
    'Vendor Bills': '仕入請求書',
    Payments: '支払',
    'Journal Entries': '仕訳',
    'Chart of Accounts': '勘定科目表',
    'Bank Account': '銀行口座',
    'Bank Accounts': '銀行口座',
    Statement: '明細',
    'Bank Statements': '銀行明細',
    Reconciliation: '照合',
    'Charge Code': '請求コード',
    'Charge Codes': '請求コード',
    'Tax Jurisdiction': '税務管轄',
    'Tax Jurisdictions': '税務管轄',
    'Tax Codes': '税コード',
    'Tax Rule': '税ルール',
    'Tax Rules': '税ルール',
    'P&L Reports': '損益計算書',
    'Balance Sheets': '貸借対照表',
    Shipment: '出荷',
    Shipments: '出荷',
    Overview: '概要',
    Accounts: 'アカウント',
    Bookings: 'ブッキング',
    Documentation: '書類',
    Release: 'リリース',
    'Local Services': 'ローカルサービス',
    Schedules: 'スケジュール',
    'Principal Tasks': '本船社タスク',
    'AI Intake': 'AI取込',
    Yard: 'ヤード',
    Moves: '移動',
    Maintenance: 'メンテナンス',
    Exceptions: '例外',
    Releases: 'リリース一覧',
    Repairs: '修理',
    Container: 'コンテナ',
    Containers: 'コンテナ',
    Cargo: '貨物',
    Costing: '原価',
    Import: '輸入',
    Export: '輸出',
    Transhipment: '積み替え',
    'Cross Trade': 'クロストレード',
    Local: 'ローカル',
    Consol: '混載',
    Air: '航空',
    'Shipment Report': '出荷レポート',
    'Billing Report': '請求レポート',
    'Imp Shipment': '輸入出荷',
    'Imp Container': '輸入コンテナ',
    'Imp Cargo': '輸入貨物',
    'Exp Shipment': '輸出出荷',
    'Exp Booking': '輸出ブッキング',
    'Exp Container': '輸出コンテナ',
    'Exp Cargo': '輸出貨物',
    'Inv / Cost Note': '請求 / コストノート',
    'Charge Lines': '料金明細',
    'Quotation / Rates': '見積 / レート',
    'Job Order': 'ジョブオーダー',
    'Job Orders': 'ジョブオーダー',
    'By Customer': '顧客別',
    'By Date': '日付別',
    'Driver Log': 'ドライバーログ',
    'Driver Pay': 'ドライバー支払',
    Employees: '従業員',
    Departments: '部署',
    Branches: '支店',
    Roles: '役割',
    Permissions: '権限',
    'General Settings': '一般設定',
    'My Profile': 'マイプロフィール',
    'Company Settings': '会社設定',
    Team: 'チーム',
    // ── CC (Command Control) nav items ──
    Home: 'ホーム',
    Tenants: 'テナント',
    'Users & Roles': 'ユーザー & ロール',
    Capabilities: '機能',
    'Capability Matrix': '機能マトリックス',
    'WhatsApp Config': 'WhatsApp設定',
    'Menu Config': 'メニュー設定',
    'Domain Configs': 'ドメイン設定',
    'Auth Control': '認証制御',
    'Command Center': 'コマンドセンター',
    'AI Team': 'AIチーム',
    Skills: 'スキル',
    'Skill Config': 'スキル設定',
    'Rules & Policies': 'ルール & ポリシー',
    'Exception Policies': '例外ポリシー',
    'Workflow Policies': 'ワークフローポリシー',
    Prompts: 'プロンプト',
    'Training Data': 'トレーニングデータ',
    'Memory Manager': 'メモリマネージャー',
    'Brain Monitor': 'ブレインモニター',
    'Brain Logs': 'ブレインログ',
    'Brain Soul': 'ブレインソウル',
    'Brain Schedule': 'ブレインスケジュール',
    'Thread Logs': 'スレッドログ',
    'Agent Lab': 'エージェントラボ',
    Messaging: 'メッセージング',
    'Email Logs': 'メールログ',
    'Email Accounts': 'メールアカウント',
    'WhatsApp Groups': 'WhatsAppグループ',
    'Platform Admin': 'プラットフォーム管理',
    'Rollout Dashboard': 'ロールアウトダッシュボード',
    'Deploy Control': 'デプロイ制御',
    'AI Control Plane': 'AIコントロールプレーン',
    'Bridge Registry': 'ブリッジレジストリ',
    'Tool Registry': 'ツールレジストリ',
    'Doc Templates': 'ドキュメントテンプレート',
    'Schema Editor': 'スキーマエディター',
    Workflows: 'ワークフロー',
    'WF Templates': 'ワークフローテンプレート',
    Scripts: 'スクリプト',
    'Config Versions': '設定バージョン',
    'Config Compiler': '設定コンパイラ',
    'View Templates': 'ビューテンプレート',
    'DSL Designer': 'DSLデザイナー',
    'Print Center': '印刷センター',
    // ── CC additional labels ──
    Rollout: 'ロールアウト',
    'AI Chat': 'AIチャット',
    'Global Users': 'グローバルユーザー',
    'User Roles': 'ユーザーロール',
    Policies: 'ポリシー',
    Schedule: 'スケジュール',
    Tools: 'ツール',
    Memory: 'メモリー',
    'Email Intake': 'メール受信',
    'Log Explorer': 'ログエクスプローラー',
    'Job Timeline': 'ジョブタイムライン',
    Bridges: 'ブリッジ',
    'Email Inbox': 'メール受信箱',
    'Platform KPI': 'プラットフォームKPI',
    'By Company': '会社別',
    'By Region': '地域別',
    'By Port Pair': '港ペア別',
    'Haulage Live': 'ハーレッジライブ',
    'Freight Volume': '貨物量',
    'WMS Throughput': 'WMSスループット',
    'Growth & Churn': '成長と離脱',
    'AI Efficiency': 'AI効率',
    // ── Driver PWA nav ──
    'My Trips': '私の便',
    'My Pay': '給与明細',
    Driver: 'ドライバー',
    // ── Module nav items (used by MODULE_NAV_ITEMS in sidebar) ──
    // Home
    Dashboard: 'ダッシュボード',
    Today: '今日',
    Inbox: '受信トレイ',
    Email: 'メール',
    'AI Review': 'AIレビュー',
    Directory: 'ディレクトリ',
    Connections: 'コネクション',
    Messages: 'メッセージ',
    Channels: 'チャンネル',
    // Business (OMS)
    Pipeline: 'パイプライン',
    'Business Party': '取引先',
    'Business Parties': '取引先',
    Customer: '顧客',
    Circles: 'サークル',
    Group: 'グループ',
    Groups: 'グループ',
    RFQ: '見積依頼',
    // Transport (TMS)
    'Event Log': 'イベントログ',
    // Haulage (HMS)
    'Control Room': 'コントロールルーム',
    'AI CS Manager': 'AI CSマネージャー',
    'Staff Performance': 'スタッフ実績',
    'TR128 Audit': 'TR128監査',
    'CO2e Report': 'CO2eレポート',
    // Freight (FMS)
    'Sea Schedule': '船舶スケジュール',
    // PRO2
    'Import Shipment': '輸入貨件',
    'Import Shipments': '輸入貨件',
    'Import Container': '輸入コンテナ',
    'Import Containers': '輸入コンテナ',
    'Import Cargo': '輸入貨物',
    'Export Shipment': '輸出貨件',
    'Export Shipments': '輸出貨件',
    'Export Booking': '輸出ブッキング',
    'Export Bookings': '輸出ブッキング',
    'Export Container': '輸出コンテナ',
    'Export Containers': '輸出コンテナ',
    'Export Cargo': '輸出貨物',
    'Invoice / Cost Note': '請求 / コストノート',
    'Invoice / Cost Notes': '請求 / コストノート',
    'Charge Line': '料金明細',
    Company: '会社',
    Companies: '会社マスタ',
    // Supply Chain (SCM)
    'Sales Order': '受注',
    'Sales Orders': '受注',
    Quotation: '見積書',
    Salesperson: '営業担当',
    Salespersons: '営業担当',
    'Purchase Order': '発注',
    Supplier: '仕入先',
    Suppliers: '仕入先',
    Product: '製品',
    Products: '製品',
    'Stock Movement': '在庫移動',
    'Stock Movements': '在庫移動',
    Fulfillment: 'フルフィルメント',
    'Shipment Tracking': '出荷追跡',
    Reports: 'レポート',
    // Service (SVC)
    'Service Order': 'サービスオーダー',
    'Service Orders': 'サービスオーダー',
    Equipment: '設備',
    Crew: 'クルー',
    Crews: 'クルー',
    Sites: '現場',
    // Warehouse (WMS)
    Tasks: 'タスク',
    'QC / Inspection': '品質検査',
    'Warehouse Tasks': '倉庫タスク',
    'Arrival Dock': '着荷ドック',
    PO: '購入注文',
    'Material Request': '資材請求',
    'Material Requests': '資材請求',
    'Material Return': '資材返品',
    'Inventory Transfer': '在庫移動',
    'Inventory Return': '在庫返品',
    'Shared Documents': '共有ドキュメント',
    '3PL Management': '3PL管理',
    Supervisor: 'スーパーバイザー',
    'Executive KPI': 'エグゼクティブKPI',
    'CS Workbench': 'CSワークベンチ',
    // Finance
    'Accounts Receivable': '売掛金',
    'Accounts Payable': '買掛金',
    'Journal Entry': '仕訳入力',
    'AR Collection': '売掛回収',
    'Payment Run': '支払処理',
    'Payment Runs': '支払処理',
    'AP Aging': '買掛金エイジング',
    'Statement of A/c': '取引明細書',
    'Statements of A/c': '取引明細書',
    'Tax Report': '税務レポート',
    'Tax Reports': '税務レポート',
    'General Ledger': '総勘定元帳',
    'Trial Balance': '試算表',
    'Profit & Loss': '損益計算書',
    'Balance Sheet': '貸借対照表',
    'Cash Flow': 'キャッシュフロー',
    'Management A/c': '管理会計',
    'Management Accounts': '管理会計',
    'Audit Report': '監査レポート',
    'Audit Reports': '監査レポート',
    'Process Audit': 'プロセス監査',
    'Process Audits': 'プロセス監査',
    Consolidation: '連結',
    'E-Invoicing': '電子インボイス',
    'Audit Overview': '監査概要',
    Settlement: '精算',
    Disputes: '紛争',
    'Bill Audit': '請求書監査',
    'Bill Audits': '請求書監査',
    'Accounting Periods': '会計期間',
    'Tax Config': '税務設定',
    'Exchange Rates': '為替レート',
    'Legal Entities': '法人',
    // Workforce (HRM)
    Attendance: '出勤',
    Payroll: '給与',
    'Leave Types': '休暇種類',
    'Leave Mgmt': '休暇管理',
    Holidays: '祝日',
    'Holiday Calendar': '祝日カレンダー',
    Loans: '貸付',
    'Claims Approval': '経費承認',
    Safety: '安全',
    Incidents: 'インシデント',
    Checklists: 'チェックリスト',
    Signoffs: '承認',
    Training: '研修',
    Briefings: 'ブリーフィング',
    'Safety Briefings': '安全ブリーフィング',
    'Work Summaries': '作業サマリー',
    'My Leave': '私の休暇',
    'My Attendance': '私の出勤',
    'My Payslip': '私の給与明細',
    'My Loans': '私の貸付',
    'My Claims': '私の経費',
    // Settings
    General: '一般',
    Profile: 'プロフィール',
    'PWA Settings': 'PWA設定',
    'PWA Install Policies': 'PWAインストールポリシー',
    'Surface Access': 'サーフェスアクセス',
    'Team Members': 'チームメンバー',
    Templates: 'テンプレート',
    'Document Templates': 'ドキュメントテンプレート',
    'Knowledge Base': 'ナレッジベース',
    Builder: 'ビルダー',
    'Builder Studio': 'ビルダースタジオ',
    'Business Packages': 'ビジネスパッケージ',
    'Package Exchange': 'パッケージ交換',
    "AI Co-CEO": "AI 共同 CEO",
    "AI Command Center": "AIコマンドセンター",
    "API Center": "APIセンター",
    "AR/AP Netting": "AR/AP ネッティング",
    "Access Log": "アクセスログ",
    "Activities": "活動内容",
    "Admin Setup": "管理者のセットアップ",
    "Agency Accounts": "代理店アカウント",
    "Alert Center": "アラートセンター",
    "Alliance": "アライアンス",
    "Alliances": "アライアンス",
    "Amber": "アンバー",
    "Appraisal": "鑑定",
    "Assignment Policies": "割り当てポリシー",
    "Bahasa Indonesia": "インドネシア語",
    "Bahasa Melayu": "バハサ・メラユ",
    "Blue": "ブルー",
    "Boox Command": "ボックスコマンド",
    "CEO Desk": "CEOデスク",
    "RM Intelligence Rules": "RM インテリジェンス ルール",
    "Commercial": "コマーシャル",
    "Contracts": "契約",
    "Daily Schedule": "一日のスケジュール",
    "Data Room": "データルーム",
    "Data Transfer": "データ転送",
    "Design Copilot": "設計副操縦士",
    "E-Invoicing Ops": "電子請求業務",
    "English": "英語",
    "Executive Nudges": "エグゼクティブナッジ",
    "Feeder Bookings": "フィーダーの予約",
    "Finance": "金融",
    "Financial Setup": "財務上のセットアップ",
    "Fixed Assets": "固定資産",
    "Fundraising": "募金活動",
    "Gate": "ゲート",
    "Green": "緑",
    "Incentives": "インセンティブ",
    "Indigo": "インディゴ",
    "Inspection": "検査",
    "Issue Orders": "命令の発行",
    "Kudos Wall": "称賛の壁",
    "LLM Intake Test": "LLM摂取テスト",
    "MCP Management": "MCP管理",
    "Messaging Intake": "メッセージングの摂取",
    "Onboarding Assignment Policies": "オンボーディング割り当てポリシー",
    "Onboarding Control": "オンボーディング制御",
    "Onboarding Records": "オンボーディング記録",
    "Part": "パート",
    "Parts": "部品",
    "Partner Applications": "パートナーアプリケーション",
    "Payroll & Compliance": "給与計算とコンプライアンス",
    "Performance Appraisals": "業績評価",
    "Pink": "ピンク",
    "Platform Analytics": "プラットフォーム分析",
    "Platform Systems": "プラットフォームシステム",
    "Port Call Docs": "ポートコールドキュメント",
    "Pricing": "価格設定",
    "Profitability": "収益性",
    "Purple": "紫",
    "REST API": "REST API",
    "Re-use Leads": "リードを再利用する",
    "Recovery": "回復",
    "Recurring Tasks": "定期的なタスク",
    "Revenue": "収益",
    "Revenue Inbox": "収益受信箱",
    "Rose": "ローズ",
    "Runtime Debug": "ランタイムデバッグ",
    "Safety Hub": "セーフティハブ",
    "Schedule Board": "スケジュールボード",
    "Settings": "設定",
    "Setup": "セットアップ",
    "Shift Swap Requests": "シフト交換リクエスト",
    "Shift Swaps": "シフト交換",
    "Site Ops": "サイト運営",
    "State Console": "ステートコンソール",
    "Stock Ledger": "株式台帳",
    "Strategy": "戦略",
    "Tally Control": "タリーコントロール",
    "Tariff": "料金",
    "Task Center": "タスクセンター",
    "Tax Configuration": "税の構成",
    "Teal": "ティール",
    "Teamspace": "チームスペース",
    "Tiếng Việt": "Tiếng Việt",
    "Tyre": "タイヤ",
    "Tyres": "タイヤ",
    "User Performance": "ユーザーパフォーマンス",
    "Vehicle": "車両",
    "WMS": "WMS",
    "WMS Setup": "WMS のセットアップ",
    "Workflow Templates": "ワークフローテンプレート",
    "ไทย": "ไทย",
    "中文": "中文",
    "日本語": "日本語",
    "繁體中文": "繁體中文",
  },

  networkPage: {
    common: {
      close: '閉じる',
      cancel: 'キャンセル',
      refresh: '更新',
      unknown: '不明',
    },
    circleTypes: {
      ownership: 'オーナーシップグループ',
      agent_network: 'エージェントネットワーク',
      vendor_panel: 'ベンダーパネル',
      peer_circle: 'ピアサークル',
      consortium: 'コンソーシアム',
    },
    groupEntityTypes: {
      hq: '本社',
      subsidiary: '子会社',
      branch: '支店',
      associate: '関連会社',
      default: '子会社',
    },
    circles: {
      memberCount: '{{count}} メンバー',
      owner: 'オーナー',
      admin: '管理者',
      detailLoadFailed: 'サークル詳細の読み込みに失敗しました',
      nameRequired: 'サークル名を入力してください',
      createdSuccess: 'サークルを作成しました',
      createFailed: 'サークルの作成に失敗しました',
      listLoadFailed: 'サークル一覧の読み込みに失敗しました',
      createTitle: '新規サークル作成',
      nameLabel: 'サークル名 *',
      namePlaceholder: '例: CMS エージェントネットワーク',
      typeLabel: 'サークル種別 *',
      descLabel: '説明（任意）',
      descPlaceholder: 'このサークルの目的を簡単に説明してください',
      createSubmitting: '作成中...',
      createAction: 'サークルを作成',
      title: 'トレードネットワーク & サークル',
      subtitle: '排他的なオーナーシップグループと、重複するトレードネットワーク・コラボレーションサークルを分離します',
      ownershipNoteBadge: 'オーナーシップグループ',
      ownershipNoteDesc: '各会社は1つのオーナーシップグループにのみ所属します。その関係は別途管理されます。',
      openGroupPage: 'グループページを開く',
      emptyTitle: 'まだサークルに参加していません',
      emptyDesc: '最初のサークルを作成してパートナーとのコラボレーションを始めましょう',
      typeCount: '{{count}} 件',
      membersTitle: 'メンバー ({{count}})',
      tradeNetworksTitle: 'トレードネットワーク',
      tradeNetworksDesc: '重複する代理店ネットワーク、混載提携、正式なビジネスネットワーク。',
      tradeNetworksEmptyDesc: 'トレードネットワークはまだありません。重複するビジネス関係のために作成してください。',
      circlesSectionTitle: 'サークル',
      circlesSectionDesc: 'トラック運送、輸送、混載、友人ベースのコラボレーショングループなどの小規模な運用サークル。',
      circlesSectionEmptyDesc: 'サークルはまだありません。ベンダーやパートナーのためにローカルコラボレーションサークルを作成してください。',
    },
    groups: {
      current: '現在',
      switch: '切替',
      loadFailed: 'グループ情報の読み込みに失敗しました',
      switchSuccess: 'ワークスペースを切り替えました。更新中...',
      switchFailed: '切替に失敗しました。再度お試しください。',
      title: 'オーナーシップグループ',
      subtitle: '各会社は1つのグループにのみ所属します。メンバー企業、本社、オフィス拠点を確認し、グループ内でワークスペースを切り替えられます。',
      emptyTitle: 'アカウントはまだどのグループにも参加していません',
      emptyDesc: 'プラットフォーム管理者に連絡して、会社をグループに追加してもらってください',
      summary: 'オーナーシップグループ · {{count}} メンバー企業',
      roleFallback: 'メンバー',
      exclusiveBadge: '排他',
      hqLabel: '本社',
      hqCompany: '本社企業',
      memberCompanies: 'メンバー企業',
      coverage: '対応国',
      companyMembersTitle: 'グループ企業',
      adminMembersTitle: 'グループ管理者 & メンバー ({{count}})',
      officeBaseValue: '{{country}} · {{city}}',
      timezoneValue: 'TZ {{timezone}}',
      currencyValue: '基準通貨 {{currency}}',
      noOfficeBase: 'オフィス拠点未設定',
    },
    rfq: {
      pageTitle: 'RFQ ワークスペース',
      pageSubtitle: 'RFQを作成するか、受信した見積依頼を確認',
      creator: '自分が作成',
      detailLoadFailed: 'RFQ詳細の読み込みに失敗しました',
      publishSuccess: 'RFQを公開しました。見積りを待っています。',
      publishFailed: 'RFQの公開に失敗しました',
      quoteAmountRequired: '見積金額を入力してください',
      quoteSubmitSuccess: '見積りを提出しました',
      quoteSubmitFailed: '見積りの提出に失敗しました',
      titleRequired: 'RFQタイトルを入力してください',
      draftCreated: 'RFQ下書きを作成しました',
      createFailed: 'RFQの作成に失敗しました',
      listLoadFailed: 'RFQ一覧の読み込みに失敗しました',
      typeSpot: 'スポットRFQ',
      typeContract: '契約RFQ',
      typeRecurring: '定期RFQ',
      modeSealed: 'シールドビッド',
      modeOpen: 'オープンビッド',
      modeMixed: 'ミックスモード',
      tabInfo: '基本情報',
      tabQuotes: '見積り',
      tabSubmit: '見積り提出',
      fieldType: 'RFQ種別',
      fieldCurrency: '通貨',
      fieldDeadline: '締切日',
      fieldCreatedAt: '作成日',
      cargoSummary: '貨物サマリー',
      publishSubmitting: '公開中...',
      publishAction: 'RFQを公開して見積りの収集を開始',
      inviteesTitle: '招待者 ({{count}})',
      emptyQuotes: '見積りはまだありません',
      awarded: '選定済み',
      submittedAt: '{{date}} に提出',
      quoteAmountLabel: '見積金額 ({{currency}}) *',
      quoteNotesLabel: '備考（任意）',
      quoteNotesPlaceholder: '条件、前提、備考など',
      submitSubmitting: '提出中...',
      submitAction: '見積りを提出',
      createTitle: '新規RFQ作成',
      formTitleLabel: 'RFQタイトル *',
      formTitlePlaceholder: '例: FCL コンテナ輸送 Q2 2026 - タンジョンパガー',
      formTypeLabel: 'RFQ種別',
      formModeLabel: '見積モード',
      formCircleLabel: '関連サークル（任意）',
      formCirclePlaceholder: '制限なし、ネットワーク全体に公開',
      formCircleAny: '制限なし、ネットワーク全体に公開',
      formDeadlineLabel: '見積締切日',
      formDescriptionLabel: '概要（任意）',
      formDescriptionPlaceholder: '貨物詳細、サービス要件、その他の備考',
      createSubmitting: '作成中...',
      createAction: '下書きを作成',
      tabMine: '自分が作成',
      tabReceived: '受信済み',
      emptyMineTitle: 'RFQはまだ作成されていません',
      emptyReceivedTitle: '見積依頼は受信されていません',
      emptyMineDesc: 'RFQを開始してサプライヤーから見積りを集めましょう',
      startAction: 'RFQを開始',
      statuses: {
        draft: '下書き',
        published: '公開済み',
        quoting: '見積中',
        evaluating: '評価中',
        awarded: '選定済み',
        completed: '完了',
        cancelled: 'キャンセル',
        expired: '期限切れ',
      },
    },
  },

  crmAccount360: {
    health: {
      attention: 'Attention',
      active: 'Active',
      steady: 'Steady',
    },
    title: 'RM Account 360',
    description: 'Commercial snapshot across contacts, deals, follow-ups, quotations, and jobs.',
    askBoox: 'Ask Boox',
    loading: 'Loading RM account snapshot...',
    loadFailed: 'Failed to load RM account 360',
    promptReviewCustomer: 'Review customer {{name}}. Summarize pipeline, overdue follow-ups, latest quotations, and next best commercial action.',
    unnamedContact: 'Unnamed contact',
    primary: 'Primary',
    decisionMaker: 'Decision maker',
    roleNotSet: 'Role not set',
    noContactChannel: 'No contact channel',
    preferred: 'Preferred: {{channel}}',
    keyContacts: 'Key Contacts',
    noContacts: 'No RM contacts yet.',
    pipelineFollowUp: 'Pipeline & Follow-up',
    noOpenActivity: 'No open RM activity for this account yet.',
    expectedClose: 'Expected close {{date}}',
    due: 'Due {{date}}',
    quotesJobs: 'Quotes & Jobs',
    noQuotesJobs: 'No quotations or jobs linked yet.',
    draft: 'draft',
    validUntil: 'Valid until {{date}}',
    jobTypeFallback: 'Job',
    eta: 'ETA {{date}}',
    cards: {
      openDeals: 'Open Deals',
      weighted: 'Weighted {{value}}',
      pipelineValue: 'Pipeline Value',
      staleDeals: 'Stale deals {{count}}',
      nextFollowUp: 'Next Follow-up',
      overdue: 'Overdue {{count}}',
      openQuotesJobs: 'Open Quotes / Jobs',
      lastTouch: 'Last touch {{date}}',
    },
    actionsTitle: 'AI Next Best Actions',
    actionsDescription: 'Account-specific commercial recommendations synthesized from follow-ups, deal freshness, quotations, and active jobs.',
    actionsEmpty: 'AI does not see an urgent next commercial action for this account yet.',
    recommendations: {
      loading: 'Loading AI priorities...',
      due: 'Due {{date}}',
      score: 'Score {{score}}',
      openAccount360: 'Open Account 360',
      priority: {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
      },
      summary: {
        critical: '{{count}} critical',
        high: '{{count}} high',
        medium: '{{count}} medium',
      },
    },
  },

  operationalIntelligence: {
    workspaceNotFound: 'Workspace not found',
    dataRecords: {
      title: 'Operational Registers',
      description: 'Maintain service, incident, warranty, inventory, safety, fleet, and signal registers as editable working tables.',
      loadFailed: 'Failed to load datasets',
      importFailed: 'Failed to import dataset',
      metrics: {
        datasets: 'Registers',
        records: 'Rows',
        uploadMode: 'Upload Mode',
      },
      uploadModes: {
        replace: 'Replace dataset',
        append: 'Append dataset',
      },
      actions: {
        quickCapture: 'Quick Capture',
        documents: 'Documents & Evidence',
        refresh: 'Refresh Data',
      },
      guidance: {
        title: 'Register guidance',
        description: 'Guidance is hidden by default to keep the workspace clean.',
      },
      datasetCard: {
        records: '{{count}} records',
        fields: '{{count}} fields',
        sourceEvidence: 'Source evidence: {{date}}',
        structure: 'Dataset Structure',
        latestSourceEvidence: 'Latest source evidence',
        storedAt: 'Stored in the shared OPI document workspace on {{date}}',
        openSourceFile: 'Open Source File',
        openLiveRecords: 'Open Live Records',
        addRow: 'Add Row',
        importing: 'Importing...',
        uploadCsv: 'Upload CSV',
      },
    },
    documents: {
      title: 'Documents & Evidence',
      description: 'Upload notes, photos, complaint files, warranty evidence, and source spreadsheets into the shared Operational Intelligence document workspace.',
      workspaceTitle: 'Operational Intelligence document workspace',
      workspaceDescription: 'This page keeps uploaded evidence live and reusable across cases, signals, and source-data imports.',
      backToSourceData: 'Back To Registers',
      uploadEnabled: 'Upload enabled',
      summary: {
        aiReady: 'AI Ready',
        aiReadyDescription: 'visible documents with AI summary or interpreted purpose',
        draftLinked: 'Draft Linked',
        draftLinkedDescription: 'visible documents that already created or linked a live draft',
        linkedRecords: 'Linked Records',
        linkedRecordsDescription: 'cross-links on the current file list into incidents, signals, cases, and evidence chains',
        pendingReview: 'Pending Review',
        pendingReviewDescription: 'visible documents still pending completion or further operator review',
      },
      whyThisViewExists: 'Why this view exists',
      intro: 'Documents are not passive attachments here. Each file can carry parse status, AI summary, suggested routing, and linked records across Operational Intelligence workflows.',
      badges: {
        parseStatus: 'Parse status shown inline',
        aiSummary: 'AI doc type + AI summary shown inline',
        suggestedRoute: 'Suggested route shown inline',
        linkedRecords: 'Linked records shown inline',
      },
    },
    quickCapture: {
      title: 'Quick Capture',
      description: 'Capture frontline signals in real time. AI will triage the record, route it into the correct operational flow, and trigger alerts when needed.',
      loadStatsFailed: 'Failed to load signal stats',
      requiredFields: 'Category, site, and subject are required.',
      captureFailed: 'Failed to capture signal',
      formTitle: 'Frontline Capture Form',
      formDescription: 'AI will classify the signal, draft a summary, and trigger alerts when thresholds are met.',
      openSignalIntake: 'Open Signal Intake',
      pushEnabled: 'Push Enabled',
      enablePushAlerts: 'Enable Push Alerts',
      stats: {
        total: 'Captured Signals',
        open: 'Open Signals',
        escalated: 'Escalated',
      },
      attachments: 'Attachments',
      trackedWorkspaceFiles: 'Tracked workspace files',
      storedInWorkspace: 'Stored in the shared Operational Intelligence document workspace',
      open: 'Open',
      guidance: {
        title: 'Capture guidance',
        description: 'Guidance is hidden by default. Expand only when you need quick reminders.',
      },
      sourceData: 'Registers',
      submitForAiTriage: 'Submit For AI Triage',
      latestCaptureReceipt: 'Latest Capture Receipt',
      linkedThroughEvidenceChain: 'Linked through the shared workspace evidence chain',
      viewFile: 'View File',
      noFileEvidence: 'This signal was submitted without file evidence.',
      openCreatedRecord: 'Open created record',
      emptyReceipt: 'Submit a signal once and the latest captured file evidence will stay visible here for traceability.',
      categoryMix: 'Category Mix',
      recentSignals: 'Recent Signals',
      noSignals: 'No signals captured yet.',
      pushHintEnabled: 'Push alerts are enabled on this device. High-priority signal escalations can land here immediately.',
      pushHintDisabled: 'Push alerts are not enabled on this device yet. Enable them above for live operational follow-up.',
    },
  },

  // ── Today page ──
  today: {
    title: '今日',
    tasks: 'タスク',
    allClear: '今日の予定はありません！',
    allClearDesc: '今日のスケジュールされたタスク、ジョブ、配送はありません。',
    jobTitle: '{{module}} #{{id}}',
    tripTitle: '便 {{id}}',
    wmsTaskTitle: 'WMS {{type}} #{{id}}',
    shipmentTitle: '{{type}} {{id}}',
  },

  // ── Module labels (standalone) ──
  module: {
    hms: 'コンテナ輸送コマンド',
    tms: 'クラシック輸送',
    wms: '倉庫フロー',
    fms: '貨物パイロット',
    scm: 'サプライチェーンナビゲーター',
    svc: 'サービス',
    hrm: '人事',
    oms: '受注',
    acc: '会計',
  },

  // ── Common ──
  common: {
    loading: '読み込み中...',
    viewAll: 'すべて見る',
    more: 'もっと',
    save: '保存',
    reset: 'リセット',
  },

  // ── Settings page ──
  settings: {
    desc: 'Boox の使用環境をカスタマイズします。',
    tabs: 'タブ',
    clearTabsOnSignout: 'サインアウト時にタブをクリア',
    clearTabsOnSignoutDesc: '有効にすると、サインアウト時にすべてのタブが閉じられます。無効にするとタブが次回セッションまで保持されます。',
    clearTabsEnabled: 'サインアウト時にタブがクリアされます',
    clearTabsDisabled: 'タブはセッション間で保持されます',
    voiceCommands: '音声コマンド',
    voiceCommandsAlwaysOn: 'PWA音声コマンド常時オン',
    voiceCommandsAlwaysOnDesc: 'PWAサイドバーにマイクトグルを追加し、オフにするまで各コマンド後もリスニングを継続します。',
    voiceCommandsEnabled: 'PWA音声コマンド有効',
    voiceCommandsDisabled: 'PWA音声コマンド無効',
    langHint: 'アプリ全体に即座に適用されます。',
    surfaceMenu: '個人サーフェスメニュー',
    surfaceMenuDesc: 'PWAとChrome拡張機能で表示するメニューカテゴリを選択します。',
    surfaceSaved: '個人サーフェスメニューを保存しました',
    surfaceSaveFailed: '個人メニューの保存に失敗しました',
    surfaceReset: '個人サーフェスメニューをリセットしました',
    surfaceResetFailed: '個人メニューのリセットに失敗しました',
    pwaMenu: 'PWAメニュー',
    pwaMenuDesc: 'go-pwaで適用されている個人メニュー。',
    extensionMenu: '拡張機能メニュー',
    extensionMenuDesc: 'Chrome拡張機能で適用されている個人メニュー。',
    categories: 'カテゴリ',
  },

  // ── Accent color ──
  accent: {
    label: 'アクセントカラー',
  },

  // ── Sidebar ──
  sidebar: {
    collapse: '折りたたむ',
    expand: '展開する',
  },

  // ── MDI Tabs ──
  mdi: {
    closeTab: 'タブを閉じる',
    closeOther: '他を閉じる',
  },

  // ── Theme ──
  theme: {
    label: 'テーマ',
    languageLabel: '言語',
    light: 'ライト',
    dark: 'ダーク',
    system: 'システム',
  },

  // ── Forgot Password ──
  forgotPassword: {
    title: 'パスワードをお忘れですか',
    desc: 'メールアドレスを入力してください。リセットリンクをお送りします。',
    submit: 'リセットリンクを送信',
    sending: '送信中…',
    sentTitle: 'メールを確認してください',
    sentDesc: '{{email}} にパスワードリセットリンクを送信しました。',
    resend: 'メールを再送信',
    backToLogin: 'ログインに戻る',
    failed: 'リセットメールの送信に失敗しました',
  },

  // ── Reset Password ──
  resetPassword: {
    title: 'パスワードのリセット',
    desc: '以下に新しいパスワードを入力してください。',
    newPassword: '新しいパスワード',
    confirmPassword: 'パスワードの確認',
    minLength: '最低8文字',
    mismatch: 'パスワードが一致しません',
    tooShort: 'パスワードは8文字以上必要です',
    submit: 'パスワードをリセット',
    resetting: 'リセット中…',
    successTitle: 'パスワードがリセットされました',
    successDesc: 'パスワードが正常にリセットされました。ログインページにリダイレクトしています…',
    goToLogin: 'ログインへ',
    failed: 'パスワードのリセットに失敗しました',
    invalidLink: '無効なリセットリンク',
    invalidLinkDesc: 'このリセットリンクは無効か、期限が切れています。',
    requestNew: '新しいリンクをリクエスト',
  },

  // ── Two-Factor Authentication ──
  twoFactor: {
    title: '2要素認証',
    emailDesc: '{{email}} に送信された6桁のコードを入力してください。',
    appDesc: '認証アプリの6桁のコードを入力してください。',
    verify: '確認',
    verifying: '確認中…',
    invalidCode: '無効な確認コード',
    resend: 'コードを再送信',
    resendIn: '{{seconds}}秒後に再送信',
    backToLogin: 'ログインに戻る',
  },

  // ── Admin panel ──
  admin: {
    title: 'AI 管理',
    groupOps: 'オペレーション',
    groupSkills: 'スキル & ツール',
    groupTraining: 'AI トレーニング',
    groupConfig: 'プラットフォーム設定',
    domainConfigs: 'ドメイン設定',
    menuConfig: 'メニュー設定',
    userRoles: 'ユーザー & ロール',
    commandCenter: 'コマンドセンター',
    brainMonitor: 'ブレインモニター',
    brainSoul: 'ブレインソウル',
    brainSchedule: 'Brain スケジュール',
    jobTimeline: 'Job タイムライン',
    agentLab: 'エージェントラボ',
    aiTeam: 'AI チーム',
    logViewer: 'ログビューアー',
    alertsApprovals: 'アラート & 承認',
    skillManager: 'スキルマネージャー',
    skillResources: 'スキルリソース',
    toolRegistry: 'ツールレジストリ',
    promptEditor: 'プロンプトエディター',
    trainingData: 'トレーニングデータ',
    memoryManager: 'メモリーマネージャー',
    docTemplates: 'ドキュメントテンプレート',
    configVersions: '設定バージョン',
    schemaEditor: 'スキーマエディター',
    workflows: 'ワークフロー',
    scriptEditor: 'スクリプトエディター',
    search: '検索',
    refresh: '更新',
    create: '作成',
    save: '保存',
    cancel: 'キャンセル',
    delete: '削除',
    edit: '編集',
    preview: 'プレビュー',
    copy: 'コピー',
    all: 'すべて',
    required: '必須',
    optional: '任意',
    noData: 'データなし',
    prompt: {
      listTitle: 'プロンプトリスト',
      searchPlaceholder: 'プロンプトを検索…',
      noMatch: '一致するプロンプトがありません',
      selectHint: '左からプロンプトを選択して編集を開始してください',
      active: 'アクティブ',
      noActiveVersion: 'アクティブなバージョンなし',
      unsavedChanges: '未保存の変更',
      params: 'パラメーター',
      history: '履歴',
      rollback: 'ロールバック',
      saveNewVersion: '新しいバージョンを保存',
      targetModel: 'ターゲットモデル',
      maxTokens: '最大トークン数',
      temperature: '温度',
      variables: '変数（カンマ区切り）',
      editorPlaceholder: 'ここにプロンプトの内容を書いてください…',
      changeNote: '変更メモ（任意）…',
      chars: '文字',
      tokens: 'トークン',
      versionHistory: 'バージョン履歴',
      noVersions: 'バージョン記録なし',
      draft: '下書き',
      archived: 'アーカイブ済み',
      load: '読み込む',
      activate: 'アクティブ化',
      uses: '{{count}} 回使用',
      quality: '品質 {{score}}%',
      emailParsingSystem: '📧 メール解析（システム）',
      emailParsingUser: '📧 メール解析（ユーザー）',
      jobCreationSystem: '🚚 ジョブ作成（システム）',
      jobCreationUser: '🚚 ジョブ作成（ユーザー）',
      jobValidationSystem: '✅ ジョブ検証（システム）',
      hsCodeSystem: '📦 HS コード（システム）',
      billingSystem: '💰 請求（システム）',
      customerInquirySystem: '💬 顧客問い合わせ（システム）',
      seaImportSystem: '🚢 海上輸入（システム）',
      nvoccSystem: '📋 NVOCC 統合（システム）',
      dgValidationSystem: '⚠️ DG 検証（システム）',
      complianceSystem: '🔒 コンプライアンスチェック（システム）',
      starterTemplate: 'スターターテンプレート — 編集して v1 として保存',
      agentRouter: '🧭 ルーターエージェント',
      agentDev: '💻 開発エージェント',
      agentOps: '⚙️ オペレーションエージェント',
      agentData: '📊 データエージェント',
      agentCoordinator: '🔗 コーディネーターエージェント',
      agentSafety: '🛡️ セーフティエージェント',
      agentInbox: '📬 受信ボックスエージェント',
      agentBilling: '💳 請求エージェント',
      agentCs: '🎧 CS エージェント',
      providerAnthropic: '🟠 Anthropic (Claude)',
      providerOpenai: '🟢 OpenAI (GPT-4)',
      providerGemini: '🔵 Google (Gemini)',
      providerDefault: '⚪ デフォルトプロバイダー',
      safetyDatabase: '🗄️ データベース安全ルール',
      safetyTenantIsolation: '🔒 テナント分離ルール',
      safetySql: '🛡️ SQL 安全ルール',
      autonomousBehavior: '🤖 自律的動作',
      docParserMbl: '📄 MBL パーサー',
      docParserHbl: '📄 HBL パーサー',
      docParserManifest: '📋 マニフェストパーサー',
      docParserNoa: '📢 NOA パーサー',
    },
    skill: {
      catalog: 'スキルリソースカタログ',
      searchPlaceholder: 'スキルを検索…',
      noData: 'スキルデータなし',
      selectHint: 'スキルを選択してリソース設定を表示',
      resources: 'リソース',
      customPoints: 'カスタムポイント',
      resourceBinding: 'リソースバインディング',
      customization: 'カスタマイズ',
      runtimePreview: 'ランタイムプレビュー',
      declared: '宣言済み',
      bound: 'バインド済み',
      isBound: 'バインド済み',
      notBound: '未バインド',
    },
    training: {
      title: 'トレーニングデータ管理',
      tabExamples: 'トレーニング例',
      tabEmbeddings: 'エンベディング',
      tabPreferences: '顧客設定',
      searchExamples: '例を検索…',
      newExample: '新規例',
      total: '合計 {{count}} 件',
      golden: 'ゴールデン',
      approved: '承認済み',
      pending: '保留中',
      rejected: '拒否',
      sampleKey: 'サンプルキー（一意のID）',
      description: '説明（任意）',
      inputLabel: '入力（JSON またはテキスト）',
      outputLabel: '期待される出力（JSON またはテキスト）',
      noExamples: 'トレーニング例なし',
      qualityLabel: '品質',
    },
    memory: {
      title: 'メモリーマネージャー',
      subtitle: '3層メモリアーキテクチャ',
      taskMemory: 'タスクメモリー',
      workspaceMemory: 'ワークスペースメモリー',
      orgMemory: '組織メモリー',
      searchPlaceholder: 'メモリキーまたはコンテンツを検索…',
      records: '{{count}} 件',
      noRecords: 'メモリレコードなし',
      noRecordsHint: 'メモリエントリはエージェント実行時に自動作成されます',
      expired: '期限切れ',
      latest: '最新',
      value: '値',
      metadata: 'メタデータ',
    },
    docTemplate: {
      title: 'ドキュメントテンプレート管理',
      templates: '{{count}} テンプレート',
      newTemplate: '新規テンプレート',
      searchPlaceholder: 'テンプレートを検索…',
      invoiceTemplate: '請求書テンプレート',
      quotationTemplate: '見積書テンプレート',
      blTemplate: 'BL テンプレート',
      doTemplate: 'DO テンプレート',
      soaTemplate: 'SOA テンプレート',
      emailTemplate: 'メールテンプレート',
      receiptTemplate: '領収書テンプレート',
      manifestTemplate: 'マニフェストテンプレート',
      newDocTemplate: '新規ドキュメントテンプレート',
      skillId: 'スキル ID',
      resourceKey: 'リソースキー',
      type: 'タイプ',
      format: 'フォーマット',
      noTemplates: 'ドキュメントテンプレートなし',
      noTemplatesHint: '「新規テンプレート」をクリックしてスキルドキュメントテンプレートを追加してください',
    },
    tool: {
      title: 'ツールレジストリ',
      toolsSkills: '{{count}} ツール/スキル',
      newDynamicSkill: '新規ダイナミックスキル',
      searchPlaceholder: 'スキル/ツールを検索…',
      nativeLabel: 'ネイティブ',
      dynamicLabel: 'ダイナミック',
      promptDriven: 'プロンプト駆動',
      toolComposed: 'ツール合成',
      sandboxBundle: 'サンドボックススクリプト',
      promptDrivenDesc: 'LLM がプロンプトテンプレートを通じて構造化出力を生成',
      toolComposedDesc: '既存のツール ID チェーンを合成して実行',
      sandboxBundleDesc: 'Python/JS サンドボックスがカスタムロジックを実行',
      slug: 'ID (スラッグ)',
      name: '名前',
      riskLevel: 'リスクレベル',
      descriptionLabel: '説明',
      intents: 'インテント（カンマ区切り）',
      promptKey: 'プロンプトキー',
      toolIdChain: 'ツール ID チェーン（カンマ区切り）',
      model: 'モデル',
      dynamicSkills: 'ダイナミックスキル',
      nativeSkills: 'ネイティブスキル',
      enabled: '有効',
      disabled: '無効',
      native: 'ネイティブ',
      dynamic: 'ダイナミック',
      intentsLabel: 'インテント',
      permissions: '権限',
      none: 'なし',
      toolChain: 'ツールチェーン',
      retry: '再試行',
      escalation: 'エスカレーション',
      version: 'バージョン',
      noData: 'ツール/スキルなし',
    },
    configVer: {
      title: '設定バージョン管理',
      versions: '{{count}} バージョン',
      defaultTemplate: 'デフォルトテンプレート',
      editPublish: '編集 & 公開',
      finalChannel: '最終チャンネル',
      experimentalChannel: '実験チャンネル',
      currentConfig: '現在のアクティブ設定',
      defaultLabel: 'デフォルト',
      cached: 'キャッシュ済み',
      tenant: 'テナント',
      layoutVersion: 'レイアウトバージョン',
      publishTime: '公開時刻',
      signature: '署名',
      menuItems: 'メニュー項目',
      pages: 'ページ',
      featureFlags: '機能フラグ',
      noConfig: '設定なし',
      publish: '公開',
      publishing: '公開中…',
      rollbackTo: 'このバージョンにロールバック',
      versionHistory: 'バージョン履歴',
      noVersionHistory: 'バージョン履歴なし',
      latest: '最新',
      current: '現在',
      publishNewVersion: '新しい設定バージョンを公開',
      publishNote: '公開後に自動署名してバージョンをインクリメントします',
    },
    userPerf: {
      title: 'ユーザー行動分析',
      subtitle: '機能採用 · AI採用 · リスクユーザーアラート',
      lastRefresh: '最終更新',
      totalUsers: '総ユーザー数',
      activeUsers: 'アクティブユーザー',
      activeUsersSub: '過去7日間アクティブ',
      riskUsers: 'リスクユーザー ⚠',
      riskUsersSub: '7日以上非アクティブ',
      avgAiAdoption: '平均AI採用率',
      avgAiAdoptionSub: 'AI操作 / 総操作',
      totalOps: '総操作数',
      featureHeat: '機能採用トップ5',
      recentEvents: '最近のビジネスイベント',
      aiDistribution: 'AI採用分布',
      aiHigh: '高 ≥20%',
      aiMid: '中 5-20%',
      aiLow: '低 <5%',
      userTable: 'ユーザーパフォーマンス詳細',
      tableSortHint: '(カラムをクリックでソート)',
      colUser: 'ユーザー',
      colTotalOps: '総操作',
      colFeatures: '使用機能',
      colActiveDays: 'アクティブ日数',
      colAiAdoption: 'AI採用率',
      colLastActive: '最終アクティブ',
      colStatus: 'ステータス',
      statusActive: 'アクティブ',
      statusInactive: '⚠ 7日非アクティブ',
      noData: 'データなし',
      days: '{{count}} 日',
      activeDays: '{{count}} 日',
      periodPrefix: '過去',
      justNow: 'たった今',
      minutesAgo: '{{count}}分前',
      hoursAgo: '{{count}}時間前',
      daysAgo: '{{count}}日前',
      last7: '過去7日間',
      last14: '過去14日間',
      last30: '過去30日間',
      last60: '過去60日間',
    },
  },

  flm: {
    page: {
      dashboard: 'フリートダッシュボード',
      assets: '車両',
      workshop: '作業指示',
      parts: '部品在庫',
      tyres: 'タイヤ',
      costing: 'フリート原価',
      reports: 'フリートレポート',
    },
    desc: {
      dashboard: '車両稼働状況、整備負荷、部品アラート、タイヤ状態、当月フリート原価を確認します。',
      assets: 'トラック、バン、トラクター、トレーラー、シャーシ、支援設備のフリート資産台帳です。',
      workshop: 'サービス依頼と修理作業指示を停止時間と原価とともに管理します。',
      parts: '整備部品の在庫、再発注点、単価、出庫履歴を管理します。',
      tyres: 'タイヤの在庫、装着、修理、リトレッド、廃棄までのライフサイクルを追跡します。',
      costing: '整備、タイヤ、燃料、固定費を車両と期間別に配賦します。',
      reports: 'km単価、停止時間、在庫移動、タイヤ寿命、コンプライアンス例外レポートです。',
    },
    action: { refresh: '更新', create: '作成', record: '記録', close: 'クローズ', send: '送信', notifyDriver: 'ドライバー通知', resend: '前回を再送' },
    error: { load: 'FLMデータの読み込みに失敗しました', save: 'FLM操作の保存に失敗しました' },
    loading: 'FLMレコードを読み込み中...',
    empty: '{{label}}レコードはまだありません。FLM基盤はテナントデータを受け入れる準備ができています。',
    table: {
      vehicles: '車両',
      workOrders: '作業指示',
      parts: '部品',
      tyres: 'タイヤ',
      costing: '原価配賦',
    },
    col: {
      plate: 'ナンバー',
      type: '種別',
      makeModel: 'メーカー / モデル',
      status: 'ステータス',
      odometer: '走行距離',
      workOrder: '作業指示',
      vehicle: '車両',
      cost: '原価',
      part: '部品',
      code: 'コード',
      stock: '在庫',
      reorder: '再発注点',
      unitCost: '単価',
      serial: 'シリアル',
      size: 'サイズ',
      position: '位置',
      date: '日付',
      category: 'カテゴリ',
      source: 'ソース',
      amount: '金額',
      action: '操作',
    },
    form: {
      assetTitle: '車両を作成',
      serviceRequestTitle: 'サービス依頼を登録',
      workOrderTitle: '作業指示を作成',
      partTitle: '部品を作成',
      stockTitle: '在庫移動を記録',
      tyreTitle: 'タイヤを作成',
      tyreEventTitle: 'タイヤイベントを記録',
      fuelTitle: '燃料費を記録',
      complianceTitle: 'コンプライアンスを記録',
      notifyDriverTitle: 'ドライバーへ通知',
      plateNo: 'ナンバー',
      assetName: '資産名',
      assetType: '資産種別',
      odometer: '走行距離 km',
      vehicle: '車両',
      selectVehicle: '車両を選択',
      serviceRequest: 'サービス依頼',
      selectServiceRequest: 'サービス依頼を選択',
      requestType: '依頼種別',
      workType: '作業種別',
      priority: '優先度',
      vendorCost: '外注費',
      miscCost: 'その他費用',
      diagnosis: '診断 / 範囲',
      description: '説明',
      partCode: '部品コード',
      partName: '部品名',
      qty: '数量',
      reorder: '再発注点',
      unitCost: '単価',
      part: '部品',
      selectPart: '部品を選択',
      movement: '移動',
      workOrder: '作業指示',
      selectWorkOrder: '作業指示を選択',
      serialNo: 'シリアル番号',
      brand: 'ブランド',
      tyreSize: 'タイヤサイズ',
      purchaseCost: '購入費',
      tyre: 'タイヤ',
      selectTyre: 'タイヤを選択',
      eventType: 'イベント種別',
      position: '位置',
      costAmount: '費用金額',
      litres: 'リットル',
      unitPrice: '単価',
      vendor: 'ベンダー',
      complianceType: 'コンプライアンス種別',
      expiryDate: '期限日',
      complianceStatus: 'コンプライアンス状態',
      notificationType: '通知種別',
      amount: '金額',
      note: 'メモ',
    },
    success: {
      asset: '車両を作成しました',
      serviceRequest: 'サービス依頼を作成しました',
      workOrder: '作業指示を作成しました',
      part: '部品を作成しました',
      stock: '在庫移動を記録しました',
      tyre: 'タイヤを作成しました',
      tyreEvent: 'タイヤイベントを記録しました',
      fuel: '燃料費を記録しました',
      compliance: 'コンプライアンスを記録しました',
      notifyDriver: 'ドライバー通知を送信しました',
      resendDriver: 'ドライバー通知を再送しました',
      close: '作業指示をクローズしました',
    },
    assetType: {
      lorry: 'トラック',
      van: 'バン',
      prime_mover: 'トラクター',
      trailer: 'トレーラー',
      forklift: 'フォークリフト',
      other: 'その他',
    },
    workType: {
      service: 'サービス',
      repair: '修理',
      breakdown: '故障',
      tyre: 'タイヤ',
      inspection: '点検',
      other: 'その他',
    },
    requestType: {
      defect: '不具合',
      service_due: '定期整備',
      breakdown: '故障',
      tyre: 'タイヤ',
      compliance: 'コンプライアンス',
      other: 'その他',
    },
    notificationType: {
      repair_completed: '修理完了',
      extra_cost_settlement: '追加費用精算',
      workshop_update: '整備更新',
      general: '一般メッセージ',
    },
    priority: {
      low: '低',
      normal: '通常',
      high: '高',
      urgent: '緊急',
    },
    movement: {
      receive: '入庫',
      issue: '作業指示へ出庫',
      adjust_in: '増加調整',
      adjust_out: '減少調整',
      return: '返品',
    },
    tyreEvent: {
      fit: '装着',
      remove: '取り外し',
      retread: 'リトレッド',
      scrap: '廃棄',
      inspect: '点検',
    },
    kpi: {
      assets: 'フリート資産',
      available: '{{count}} 台稼働可能',
      blocked: '利用不可車両',
      blockedHint: '整備中、故障、またはコンプライアンス保留',
      openWorkOrders: '未完了作業指示',
      lowStockParts: '{{count}} 件の低在庫部品',
      monthCost: '当月原価',
      fittedTyres: '{{count}} 本装着中',
    },
    status: {
      active: '稼働中',
      workshop: '整備中',
      breakdown: '故障',
      compliance_hold: 'コンプライアンス保留',
      off_road: '運行停止',
      sold: '売却済み',
      draft: '下書き',
      open: 'オープン',
      in_progress: '進行中',
      waiting_parts: '部品待ち',
      vendor_work: '外注作業',
      completed: '完了',
      closed: 'クローズ',
      cancelled: 'キャンセル',
      in_stock: '在庫中',
      fitted: '装着中',
      repair: '修理',
      retread: 'リトレッド',
      scrapped: '廃棄',
      expiring: '期限間近',
      hold: '保留',
      expired: '期限切れ',
      waived: '免除',
      triaged: '仕分け済み',
      approved: '承認済み',
      waiting_driver: 'ドライバー待ち',
    },
    panel: {
      recentRequests: '最近のサービス依頼',
      complianceAlerts: 'コンプライアンス注意',
      driverNotifications: 'ドライバー WhatsApp 履歴',
    },
    next: {
      title: '次の実装スライス',
      workshopTitle: '作業指示 MVP',
      workshopText: '修理作業指示を作成し、原価計算してクローズします。',
      inventoryTitle: '部品とタイヤ',
      inventoryText: '部品出庫とタイヤ装着、ローテーション、修理、廃棄を追跡します。',
      dispatchTitle: '配車可用性シグナル',
      dispatchText: '利用不可のフリート資産をTMS/HMS/SVCの割当から除外します。',
    },
  },

  tenantAdmin: {
    title: 'テナント管理',
    subtitle: 'テナントのモジュール、プラン、アクセスを管理',
    back: '戻る',
    edit: '編集',
    save: '保存',
    cancel: 'キャンセル',
    refresh: '更新',
    core: 'コア',
    saveChanges: '変更を保存',
    authControl: '認証制御',
    connectorSetup: 'コネクター設定',
    skillPolicies: 'スキルポリシー',
    manageConnectors: 'コネクター管理',
    managePolicies: 'ポリシー管理',
    tenantInfo: 'テナント情報',
    operationalModules: 'オペレーショナルモジュール',
    modulesDesc: 'このテナントがアクセスできるモジュールを制御します。コアモジュール（「コア」と表示）は常に有効です。',
    tenantMembers: 'テナントメンバー',
    loading: '読み込み中…',
    loadingTenants: 'テナントを読み込み中…',
    loadingConnectors: 'コネクターを読み込み中…',
    noMembers: 'メンバーなし',
    noConnectors: 'コネクターがまだ設定されていません。',
    noTenantsMatch: 'フィルターに一致するテナントなし',
    searchPlaceholder: '名前またはスラッグで検索…',
    allTenants: 'すべてのテナント',
    logisticsTenants: '物流テナント（FMSなし）',
    freightTenants: '賓物テナント（FMSあり）',
    fields: {
      name: '名前', slug: 'Slug', plan: 'プラン', status: 'ステータス',
      currency: '通貨', locale: 'ロケール', timezone: 'タイムゾーン',
      country: '国', taxId: '税務ID', companyRegNo: '会社登録番号',
      businessDesc: '事業内容',
    },
    placeholders: {
      taxId: '例: GST登録番号', companyRegNo: '例: UEN 201234567M',
      businessDesc: '会社の事業内容の簡単な説明…',
    },
    moduleCategory: {
      business: 'ビジネス管理',
      vertical: '業種別モジュール',
      integration: '連携 & コネクター',
    },
    modules: {
      OMS: '受注 (OMS)', CRM: 'Relationship Management', INBOX: '受信トレイ (INBOX)', AIC: 'AIチャット (AIC)', OPI: '運用インテリジェンス (OPI)', AI: 'AI Agentic Control (AI)', TA: 'Task Management (TA)', RM: 'Relationship Management (RM)', TM: 'Transport Hero (TM)', TP: 'Transport Planning (TP)', CT: 'コンテナ輸送 (CT)', WM: '倉庫管理 (WM)', CF: 'CFS倉庫 (CF)', FM: 'フレイト管理 (FM)', SC: 'Supply Chain (SC)', MT: 'Maritime & Port Agency (MT)', TMS: '輸送 (TMS)',
      FLM: 'フリート管理 (FLM)',
      HMS: 'コンテナ輸送 (HMS)', DMS: 'デポ (DMS)', FMS: '貨物 (FMS)', SAS: '船会社代理店 (SAS)', WMS: '倉庫 (WMS)',
      HRM: '人事 (HRM)', ACC: '会計 (ACC)', SET: '設定 (SET)', PRO2: 'PRO2レガシーブラウザ',
      SCM: 'サプライチェーン (SCM)',
      SVC: 'サービス管理 (SVC)',
      SOC: 'シッパー所有コンテナ (SOC)',
      CW: 'CargoWise コネクター',
      CERP: 'CargoERP コネクター',
      EZSH: 'EZShip コネクター',
      SFRT: 'SysFreight コネクター',
      KDCFS: 'KDCFS コネクター',
      QB: 'QuickBooks コネクター',
      XERO: 'Xero コネクター',
      ORCL: 'Oracle ERP コネクター',
      SAP: 'SAP コネクター',
      NETSUITE: 'NetSuite コネクター',
      FR8: 'FR8LAN コネクター',
      FLINK: 'FreightLink コネクター',
      KFLD: 'Keyfield コネクター',
      OPTTRK: 'Opt-E-Truck コネクター',
      OPTHUB: 'Opt-E-Hub コネクター',
    },
    moduleDesc: {
      OMS: 'コアビジネス: 受注、見積、パイプライン、顧客',
      CRM: 'Relationship intelligence: パイプライン、連絡先、Account 360、リカバリー、セルフサービス信号',
      INBOX: 'メッセージ、メール、WhatsApp、タスク、承認',
      AIC: 'AIコパイロットチャット、サイドパネル、エージェント会話ワークスペース',
      OPI: 'AI運用インテリジェンス: パフォーマンス分析、インシデント、サービス信号、在庫異常、EHS、管理ブリーフ',
      AI: 'SDK-governed agentic control: AI workbench, prompt profiles, approvals, context and audit',
      TA: 'SDK menu-set for tasks, actions, schedules, attachments, assignment and reports',
      RM: 'Relationship Management: account, contact, supplier, partner, and service relationship layer',
      TM: '新しい動的輸送プロファイル: 受信トレイ、Job Order、Trips、Billing、Planner、PWAタスク、共有、レポート',
      TP: 'AI Agentic native transport planning: planner, constraints, costing, documents and approval-gated AI proposals',
      CT: '新しい動的コンテナ輸送プロファイル: Job Order、コンテナ、Trips、ドライバーチーム、PWAタスク、共有、請求、レポート',
      WM: '新しい動的倉庫プロファイル: 入庫、在庫、ピッキング、棚入れ、時間別リソース計画、PWAタスク、共有、レポート',
      CF: '新しい動的CFS倉庫プロファイル: コンテナ、貨物タリー、デバン、バンニング、保管、リリース、FM連携、PWAタスク、共有、請求',
      FM: '新しい動的フレイトプロファイル: Shipment、書類、価格、原価、請求連携、共有、レポート',
      SC: 'Supply chain profile: order, supplier, procurement, fulfillment, inventory and document control',
      MT: 'Maritime and port agency profile: vessel, port call, berth, tally, documents, service orders and disbursement',
      TMS: 'ローカル配送、トラック輸送、プランナー、配車',
      HMS: 'FCLコンテナ輸送、港湾業務、IMP/RET、COL/EXP',
      FLM: '車両、整備工場、部品、タイヤ、コンプライアンス、フリート原価',
      DMS: 'デポゲート/ヤード/検査/移動/請求',
      FMS: '海上/航空輸入輸出、CFS、通関、レート',
      SAS: '船会社代理店業務、顧客デスク、リリース対応、プリンシパル連携',
      WMS: '入庫、在庫、出庫、作業指示',
      HRM: '従業員、勤怠管理、給与、休暇、人事セルフサービス',
      ACC: 'AR/AP、仕訳、税コード、財務レポート',
      SET: '会社設定、チーム、請求、テンプレート',
      PRO2: 'PRO2/CargoConnectレガシーデータの読取り専用ブラウザ',
      SCM: '販売注文、発注書、在庫、製品カタログ、フルフィルメント',
      SVC: 'サービス受注、機器/資産管理、班編成、現場作業',
      SOC: 'シッパー所有コンテナ管理 — 常時有効',
      CW: 'WiseTech CargoWise One / ediEnterprise連携',
      CERP: 'CargoERPレガシーシステムデータブリッジ',
      EZSH: 'EZShip配送プラットフォームデータブリッジ',
      SFRT: 'SysFreight ERPデータブリッジ',
      KDCFS: 'KDCFS CFSシステムデータブリッジ',
      QB: 'Intuit QuickBooks会計同期',
      XERO: 'Xeroクラウド会計同期',
      ORCL: 'Oracle ERP / NetSuite連携',
      SAP: 'SAP S/4HANA / Business One連携',
      NETSUITE: 'Oracle NetSuite ERP連携',
      FR8: 'FR8LANフレイトマーケットプレイスコネクター',
      FLINK: 'FreightLink予約・追跡コネクター',
      KFLD: 'Keyfield Logistics システムコネクター',
      OPTTRK: 'Opt-E-Truck車両管理コネクター',
      OPTHUB: 'Opt-E-Hub倉庫管理コネクター',
    },
    stats: {
      totalJobs: '合計ジョブ', members: 'メンバー',
      lastActive: '最終アクティブ', connectors: 'コネクター',
    },
    summary: {
      totalTenants: 'テナント総数', active: 'アクティブ',
      freight: '賓物 (FMS)', totalJobs: '合計ジョブ',
    },
    filter: { all: 'すべて', logistics: '物流', freight: '賓物' },
    listCols: {
      tenant: 'テナント', status: 'ステータス', plan: 'プラン',
      modules: 'モジュール', jobs: 'ジョブ', lastActivity: '最終アクティビティ',
    },
    connectorCols: {
      connector: 'コネクター', type: 'タイプ', endpoint: 'APIエンドポイント',
      policy: 'ポリシー', mode: 'モード', status: 'ステータス',
    },
    memberCols: {
      name: '名前', email: 'メール', role: 'ロール',
      joined: '参加日', actions: 'アクション',
    },
    toast: {
      modulesSaved: 'モジュールを保存しました',
      modulesSaveFailed: 'モジュールの保存に失敗しました',
      tenantInfoSaved: 'テナント情報を保存しました',
      tenantInfoSaveFailed: 'テナント情報の保存に失敗しました',
      memberUpdated: 'メンバーを更新しました',
      memberUpdateFailed: 'メンバーの更新に失敗しました',
      switchedTenant: 'テナントコンテキストを {{name}} に切り替えました',
      switchFailed: 'テナントコンテキストの切り替えに失敗しました',
      loadMembersFailed: 'メンバーの読み込みに失敗しました',
    },
  },

  // ── SVC (サービス管理) ──
  svc: {
    status: {
      DRAFT: '下書き', CONFIRMED: '確認済み', SCHEDULED: '予定済み', DISPATCHED: '配車済み',
      IN_PROGRESS: '作業中', COMPLETED: '完了', INVOICED: '請求済み', CANCELLED: 'キャンセル',
      AVAILABLE: '利用可能', IN_USE: '使用中', MAINTENANCE: 'メンテナンス', RETIRED: '廃止',
      ON_DUTY: '勤務中', OFF_DUTY: '勤務外', ON_LEAVE: '休暇中', ACTIVE: '有効', INACTIVE: '無効',
    },
    serviceType: {
      crane_lift: 'クレーン作業', rigging: 'リギング', transport: '運搬', installation: '設置',
      maintenance: 'メンテナンス', inspection: '点検', other: 'その他',
    },
    priority: { low: '低', normal: '通常', high: '高', urgent: '緊急' },
    equipmentType: {
      crane: 'クレーン', forklift: 'フォークリフト', truck: 'トラック', generator: '発電機',
      scaffold: '足場', tool_set: '工具セット', other: 'その他',
    },
    field: {
      customer: '顧客', site: '現場', serviceType: 'サービス種別', priority: '優先度',
      scheduledDate: '予定日', estHours: '見積時間', description: '説明', remarks: '備考',
      assetCode: '資産コード', name: '名称', type: '種別', capacity: '能力', serialNo: 'シリアル番号',
      location: '所在地', nextServiceDate: '次回点検日', code: 'コード', crewName: '班名',
      teamLeader: '班長', skills: 'スキル', siteCode: '現場コード', siteName: '現場名',
      contact: '連絡先', phone: '電話', postalCode: '郵便番号', address: '住所',
    },
    kpi: { totalOrders: '受注総数', scheduled: '予定済み', inProgress: '作業中', completed: '完了' },
    tab: {
      recentOrders: '最近の受注', activeDispatched: '進行中 / 配車済み', allOrders: 'すべての受注',
      draft: '下書き', active: '進行中', pendingDispatch: '配車待ち', dispatched: '配車済み',
      allEquipment: 'すべての機器', available: '利用可能', maintenance: 'メンテナンス',
      allCrews: 'すべての班', allSites: 'すべての現場', allInvoices: 'すべての請求書',
      pendingBilling: '請求待ち', costBreakdown: '原価内訳', summary: 'サマリー',
    },
    col: {
      orderNo: '受注番号', customer: '顧客', site: '現場', type: '種別', scheduled: '予定日',
      status: 'ステータス', crew: '班', date: '日付', equipment: '機器', priority: '優先度',
      estHrs: '見積時間', created: '作成日', assetCode: '資産コード', name: '名称', capacity: '能力',
      location: '所在地', nextService: '次回点検', lastService: '前回点検', code: 'コード',
      crewName: '班名', teamLeader: '班長', members: 'メンバー', skills: 'スキル',
      siteName: '現場名', address: '住所', contact: '連絡先', hours: '時間',
      materials: '材料費', total: '合計', labor: '人件費', totalCost: '総コスト',
      revenue: '売上', marginPct: '利益率', period: '期間', orders: '受注数',
      completed: '完了', cost: 'コスト', avgHrsJob: '平均時間/件', serialNo: 'シリアル番号',
      phone: '電話', postalCode: '郵便番号',
    },
    action: {
      confirm: '確認', schedule: '予定設定', dispatch: '配車', startWork: '作業開始',
      complete: '完了', invoice: '請求', cancel: 'キャンセル',
      cancelConfirm: 'このサービス受注をキャンセルしますか？',
    },
    page: {
      dashboard: 'サービス管理', orders: 'サービス受注', dispatch: '配車管理',
      equipment: '機器管理', crew: '班管理', sites: '現場管理', billing: 'サービス請求',
      costing: 'サービス原価', reports: 'サービスレポート',
    },
    dialog: {
      newOrder: '新規サービス受注', addEquipment: '機器追加', addCrew: '班追加', addSite: '現場追加',
    },
  },
  chat: {
    searchPlaceholder: '検索…',
    newChat: '新しい会話',
    noSessions: 'まだ会話がありません。\n+ をクリックして始めましょう。',
    noSearchResults: '一致する会話がありません',
    archive: 'アーカイブ',
    delete: '削除',
    archivedCount: 'アーカイブ済み ({{count}})',
    restore: '復元',
    managePrompts: 'クイックプロンプト管理',
    promptLabel: '短い名前（例：📋 今日のまとめ）',
    promptContent: 'AIに聞く質問を入力…',
    autoExecute: 'クリックで即送信',
    noPrompts: 'クイックプロンプトがありません。「追加」をクリックして作成してください。',
    confirmDelete: '「{{label}}」を削除しますか？',
    deleteFailed: '会話の削除に失敗しました',
    archived: '会話をアーカイブしました',
    restored: '会話を復元しました',
    chatTitle: 'チャット',
    thinking: '考え中…',
    share: '会話を共有',
    typeToStart: '下に入力して会話を始めましょう',
    startTyping: '何か話してみましょう…',
    quickPrompts: 'クイックプロンプト',
    inputPlaceholder: '何でも聞いてください… (Enterで送信, Shift+Enterで改行)',
    stopRecording: 'タップして停止',
    voiceInput: 'タップして話す',
    stop: '生成を停止',
    disclaimer: 'AIの回答は正確でない場合があります。重要な情報は必ずご確認ください。',
    shareTitle: '会話を共有',
    selectMessages: 'メッセージを選択',
    shareExpiry: 'リンクの有効期限',
    generateLink: '共有リンクを生成',
    copied: 'コピーしました！',
    copy: 'コピー',
    sharedConversation: '共有された会話',
    shareWhatsApp: 'WhatsAppで共有',
    shareInfo: 'このリンクを持つ人は誰でもログインなしで会話を閲覧できます。',
    expiresAt: '有効期限',
  },

  finance: {
    method: {
      bank_transfer: '銀行振込',
      cheque: '小切手',
      cash: '現金',
      paynow: 'PayNow',
      fps: 'FPS',
      other: 'その他',
    },
    col: {
      document: '伝票',
      method: '方法',
      date: '日付',
      amount: '金額',
      unallocated: '未消込',
      allocated: '消込済',
      status: 'ステータス',
    },
    kpi: {
      documents: '伝票数',
      unallocatedExposure: '未消込残高',
    },
    controls: {
      title: '決済コントロール',
      committedCash: '確定済キャッシュフロー',
      allocationOpen: '消込が未完了',
      unallocatedAmount: '未消込金額',
      requiresApproval: '承認が必要',
      none: 'なし',
      yes: 'はい',
      no: 'いいえ',
    },
    action: {
      assist: 'アシスト',
      refresh: '更新',
      void: '無効化',
      reverse: '取消',
      apply: '消込',
    },
    dock: {
      header: '{{title}} ヘッダー',
      appliedAllocations: '消込済明細',
      noAllocations: '消込明細はまだありません。',
      lineBreakdown: '支払行明細',
      noLines: '行明細はありません。',
      outstandingDocs: '未消込対応伝票',
      noOutstanding: 'この取引先の{{type}}未消込伝票はありません。',
      outstandingAmt: '未消込',
      noDueDate: '期日なし',
    },
    toast: {
      allocationApplied: '消込が適用されました',
      allocationReversed: '消込が取り消されました',
      noUnallocated: '消込可能な残高がありません',
      loadFailed: '{{entity}}の読み込みに失敗しました',
      allocationFailed: '消込に失敗しました',
      reverseFailed: '取消に失敗しました',
      voidConfirm: 'この{{kind}}を無効化しますか？既存の消込は取り消されます。',
      voided: '{{title}}が無効化されました',
      voidFailed: '{{entity}}の無効化に失敗しました',
    },
    empty: {
      selectRecord: 'レコードを選択すると消込状況と対応伝票を確認できます。',
    },
  },
} as const;

export default ja;
